import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home, Brain, ArrowRight, Phone, Shield, AlertCircle, CheckCircle2 } from "lucide-react";
import { Header, Footer } from "../../../components/Layout";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";

const PatologiaDualPage = () => {
  const comorbidities = [
    { disorder: "Depresión mayor", subst: "Alcohol, benzodiacepinas, cocaína", desc: "La sustancia funciona inicialmente como \"automedicación\" y luego agrava el cuadro." },
    { disorder: "Trastorno bipolar", subst: "Cocaína, alcohol, cannabis", desc: "Consumo impulsivo en fases maníacas, depresión y recaídas frecuentes." },
    { disorder: "Ansiedad generalizada / Pánico", subst: "Benzodiacepinas, alcohol, cannabis", desc: "Alta tasa de dependencia a ansiolíticos por prescripción médica mal controlada." },
    { disorder: "TDAH en adultos", subst: "Cocaína, anfetaminas, cannabis", desc: "Búsqueda de estimulantes para \"concentrarse\"; requiere evaluación neuropsiquiátrica." },
    { disorder: "Trastorno límite de personalidad (TLP)", subst: "Alcohol, cocaína, polisustancias", desc: "Consumo impulsivo asociado a inestabilidad emocional y autolesiones." },
    { disorder: "Esquizofrenia / Psicosis", subst: "Cannabis, cocaína, pasta base", desc: "Requiere hospitalización psiquiátrica y antipsicóticos de inicio." },
    { disorder: "TEPT / Trauma", subst: "Alcohol, opioides, benzodiacepinas", desc: "Consumo para evitar flashbacks; tratamiento integrado es esencial." }
  ];

  const whatToLook = [
    "Psiquiatra dentro del equipo clínico (no solo psicólogo).",
    "Experiencia demostrable en tratamiento integrado (addiction + mental health).",
    "Capacidad para ajustar medicación psiquiátrica durante internación.",
    "Evaluación diagnóstica inicial con pruebas psicológicas y neuropsiquiátricas.",
    "Abordaje familiar y psicoeducación sobre ambas condiciones.",
    "Derivación a neurología o endocrinología si es necesario."
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Patología Dual: Tratamiento Integrado de Adicciones y Salud Mental en Chile | SinAdicciones.org</title>
        <meta name="description" content="Patología dual: combinación de adicción y trastorno psiquiátrico. Guía sobre diagnóstico integrado y centros de rehabilitación en Chile con equipo psiquiátrico especializado." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/patologia-dual" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/patologia-dual" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <meta property="og:title" content="Patología Dual en Chile: Centros con Tratamiento Integrado" />
        <meta property="og:description" content="Cuando la adicción convive con depresión, bipolaridad, ansiedad o psicosis. Tratamiento integrado, no paralelo." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalCondition",
          "name": "Patología Dual",
          "alternateName": ["Diagnóstico dual", "Comorbilidad psiquiátrica", "Dual diagnosis"],
          "description": "Presencia simultánea de un trastorno por consumo de sustancias y un trastorno psiquiátrico (depresión, bipolaridad, ansiedad, TDAH, TLP, psicosis).",
          "possibleTreatment": [
            { "@type": "MedicalTherapy", "name": "Tratamiento integrado dual" },
            { "@type": "MedicalTherapy", "name": "Psiquiatría de adicciones" },
            { "@type": "MedicalTherapy", "name": "Internación psiquiátrica" },
            { "@type": "MedicalTherapy", "name": "Terapia dialéctica conductual" }
          ]
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16" data-testid="patologia-dual-page">
        {/* Breadcrumbs */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/adicciones" className="hover:text-emerald-400">Adicciones</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300">Patología Dual</span>
            </nav>
          </div>
        </div>

        <section className="bg-gradient-to-b from-purple-950/30 to-slate-950 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-xs font-medium mb-4">
              <Brain className="w-3 h-3" /><span>Diagnóstico integrado</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="dual-title">
              Patología Dual: Adicción + Trastorno Psiquiátrico
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">
              Cuando la adicción convive con un trastorno de salud mental (depresión, ansiedad, bipolaridad, TDAH, TLP, psicosis), tratarlas por separado casi nunca funciona.
              La <strong>patología dual</strong> requiere equipos con psiquiatra, protocolo integrado y medicación ajustada durante la internación.
            </p>
          </div>
        </section>

        {/* Comorbidities */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold mb-2">Combinaciones más comunes en Chile</h2>
          <p className="text-slate-400 mb-8">Identificar el trastorno psiquiátrico subyacente es el primer paso para un tratamiento efectivo.</p>
          <div className="space-y-3">
            {comorbidities.map(function(c, i) {
              return (
                <Card key={i} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-50">{c.disorder}</h3>
                          <span className="text-xs text-slate-500">+ {c.subst}</span>
                        </div>
                        <p className="text-sm text-slate-400">{c.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* What to look for */}
        <section className="max-w-5xl mx-auto px-4 py-8">
          <h2 className="text-3xl font-bold mb-2">Qué buscar en un centro</h2>
          <p className="text-slate-400 mb-8">No todos los centros de rehabilitación pueden tratar patología dual. Verifica estos elementos:</p>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-6">
              <ul className="space-y-3">
                {whatToLook.map(function(item, i) {
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{item}</span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Warning */}
        <section className="max-w-5xl mx-auto px-4 py-8">
          <div className="bg-red-950/30 border border-red-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-red-300 mb-2">Tratamiento paralelo: por qué no funciona</h3>
                <p className="text-sm text-slate-300">
                  Muchos pacientes llegan a centros de adicciones tras ser "dados de alta" de salud mental, o viceversa. Este ping-pong agrava ambas condiciones.
                  El estándar de oro es un <strong>tratamiento integrado</strong>: mismo equipo, mismo lugar, ambos diagnósticos al mismo tiempo.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/20 rounded-2xl p-8">
            <Shield className="w-12 h-12 text-emerald-400 mb-4" />
            <h2 className="text-2xl font-bold mb-3">Busca centros con equipo psiquiátrico</h2>
            <p className="text-slate-400 mb-6">Filtra por centros con capacidad para tratar patología dual en el directorio. Comunidades terapéuticas y hospitales son habitualmente los más indicados.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                <Link to="/centros-de-rehabilitacion-drogas-chile?specialization=patologia-dual" data-testid="dual-cta-centros">Ver centros con patología dual <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10">
                <a href="tel:1412" data-testid="dual-fonodrogas"><Phone className="w-4 h-4 mr-2" />Llamar Fonodrogas 1412</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="max-w-5xl mx-auto px-4 py-8">
          <h3 className="text-lg font-semibold mb-4">Relacionados</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/adicciones/alcohol" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Alcohol</Link>
            <Link to="/adicciones/cocaina" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Cocaína</Link>
            <Link to="/adicciones/pasta-base" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Pasta Base</Link>
            <Link to="/adicciones/medicamentos" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Benzodiacepinas / Medicamentos</Link>
            <Link to="/tratamiento/residencial" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Tratamiento residencial</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PatologiaDualPage;
